import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

// URL paths that embed bearer tokens (password-reset, email-verify, payment
// link). Replay/Breadcrumb/Error events all capture the current URL; without
// scrubbing, Sentry org members + anyone with breached Sentry access could
// read live tokens out of replays and replay them before the legitimate
// user. We redact the token segment to a fixed placeholder before any
// outbound event leaves the browser.
const TOKEN_URL_PATTERNS = [
  /\/account\/reset-password\/[^/?#]+/g,
  /\/account\/verify-email\/[^/?#]+/g,
  /\/pay\/[^/?#]+\/[^/?#]+/g,
];

function scrubUrl(url) {
  if (typeof url !== 'string') return url;
  let cleaned = url;
  for (const re of TOKEN_URL_PATTERNS) {
    cleaned = cleaned.replace(re, (m) => {
      // Preserve the route prefix; redact the trailing token segment(s).
      const lastSlash = m.lastIndexOf('/');
      return m.slice(0, lastSlash) + '/[REDACTED]';
    });
  }
  return cleaned;
}

if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.NODE_ENV,
    release: process.env.REACT_APP_VERSION,
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
    // Scrub bearer-token URLs from error events before send.
    beforeSend(event) {
      if (event?.request?.url) {
        event.request.url = scrubUrl(event.request.url);
      }
      return event;
    },
    // Scrub URLs from breadcrumbs (navigation, fetch, xhr).
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb?.data?.url) {
        breadcrumb.data.url = scrubUrl(breadcrumb.data.url);
      }
      if (breadcrumb?.data?.to) {
        breadcrumb.data.to = scrubUrl(breadcrumb.data.to);
      }
      if (breadcrumb?.data?.from) {
        breadcrumb.data.from = scrubUrl(breadcrumb.data.from);
      }
      if (typeof breadcrumb?.message === 'string') {
        breadcrumb.message = scrubUrl(breadcrumb.message);
      }
      return breadcrumb;
    },
  });
}

// Top-level fallback shown if a render error escapes every component-local boundary.
// Sentry already captured the exception via its ErrorBoundary; this just keeps the
// page from going blank-white.
function GlobalErrorFallback({ resetError }) {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      color: '#1e293b',
      textAlign: 'center'
    }}>
      <h1 style={{ fontSize: '24px', marginBottom: '12px' }}>Something went wrong</h1>
      <p style={{ color: '#64748b', maxWidth: '480px', marginBottom: '24px' }}>
        The team has been notified. Try reloading the page, or come back in a few minutes.
      </p>
      <div style={{ display: 'flex', gap: '12px' }}>
        <button
          onClick={() => { resetError(); window.location.reload(); }}
          style={{
            padding: '10px 20px',
            background: '#2563eb',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          Reload page
        </button>
        <a
          href="/"
          style={{
            padding: '10px 20px',
            background: '#f1f5f9',
            color: '#1e293b',
            border: '1px solid #e2e8f0',
            borderRadius: '6px',
            fontSize: '14px',
            textDecoration: 'none'
          }}
        >
          Go home
        </a>
      </div>
    </div>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={GlobalErrorFallback}>
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
